export interface TextSegment {
  readonly text: string;
  readonly startsBlock: boolean;
}

export interface SegmentPoint {
  readonly segment: number;
  readonly offset: number;
}

export interface SegmentRange {
  readonly start: SegmentPoint;
  readonly end: SegmentPoint;
}

interface TextRange {
  readonly start: number;
  readonly end: number;
}

const QUOTE_CHARS = /["«»“”„‟'‘’‚‛]/u;
const DASH_CHARS = /[‐‑‒–—―−]/u;
const ELLIPSIS = /\s*(?:\[\s*(?:\.\.\.|…)\s*\]|\.\.\.|…)\s*/u;
const MIN_QUOTE_LENGTH = 20;

const canonicalChars = new Map<string, string>();

function canonicalChar(char: string): string {
  if (/\s/u.test(char)) return "";
  if (QUOTE_CHARS.test(char)) return '"';
  if (DASH_CHARS.test(char)) return "-";
  if (char === "№") return "n";
  const lower = char.toLocaleLowerCase("ru");
  if (lower === "ё") return "е";
  return lower.length === 1 ? lower : char;
}

function cachedCanonicalChar(char: string): string {
  let canonical = canonicalChars.get(char);
  if (canonical === undefined) {
    canonical = canonicalChar(char);
    canonicalChars.set(char, canonical);
  }
  return canonical;
}

function normalizeWithMap(value: string): { normalized: string; map: number[] } {
  const map: number[] = [];
  let normalized = "";
  let pendingSpace = false;
  for (let index = 0; index < value.length; index += 1) {
    const char = cachedCanonicalChar(value[index] ?? "");
    if (char === "") {
      pendingSpace = normalized.length > 0;
      continue;
    }
    if (pendingSpace) {
      normalized += " ";
      map.push(index);
      pendingSpace = false;
    }
    normalized += char;
    map.push(index);
  }
  return { normalized, map };
}

function findFragment(
  haystack: { normalized: string; map: number[] },
  fragment: string,
): TextRange | null {
  const needle = normalizeWithMap(fragment).normalized.replace(/^["\s]+|["\s]+$/gu, "");
  for (const candidate of [needle, needle.replace(/["\s.;,:]+$/u, "")]) {
    if (candidate.length < MIN_QUOTE_LENGTH) continue;
    const index = haystack.normalized.indexOf(candidate);
    if (index === -1) continue;
    const start = haystack.map[index];
    const last = haystack.map[index + candidate.length - 1];
    if (start === undefined || last === undefined) continue;
    return { start, end: last + 1 };
  }
  return null;
}

function pointAt(segmentStarts: ReadonlyArray<number>, index: number): SegmentPoint {
  let low = 0;
  let high = segmentStarts.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if ((segmentStarts[middle] ?? 0) <= index) low = middle;
    else high = middle - 1;
  }
  return { segment: low, offset: Math.max(0, index - (segmentStarts[low] ?? 0)) };
}

export function findQuoteRangesInSegments(
  segments: ReadonlyArray<TextSegment>,
  quotes: ReadonlyArray<string>,
): SegmentRange[] {
  if (quotes.length === 0) return [];
  let text = "";
  const segmentStarts: number[] = [];
  for (const segment of segments) {
    if (segment.startsBlock && text.length > 0) text += "\n";
    segmentStarts.push(text.length);
    text += segment.text;
  }
  const haystack = normalizeWithMap(text);
  return quotes
    .flatMap((quote) => quote.split(ELLIPSIS))
    .flatMap((fragment) => {
      const range = findFragment(haystack, fragment);
      if (!range) return [];
      const start = pointAt(segmentStarts, range.start);
      const last = pointAt(segmentStarts, range.end - 1);
      return [{ start, end: { segment: last.segment, offset: last.offset + 1 } }];
    });
}
